/* eslint-disable import/order -- the `react-semantic-ui-datepickers` stylesheet is a
   SIDE-EFFECT import, and the ordering this rule asks for moves module imports across
   it. Stylesheet order is cascade order, so that is a change to what the page renders,
   not to its types. */
import { useState, useEffect } from 'react';
import SemanticDatepicker from 'react-semantic-ui-datepickers';
import type { SemanticDatepickerProps } from 'react-semantic-ui-datepickers/dist/types';
import {
  Header,
  Button,
  Icon,
  Modal,
  Form,
  Input,
  Select,
  Message,
  Dimmer,
  Loader,
  Segment,
  Checkbox,
  Confirm,
} from 'semantic-ui-react';
import { v4 as uuid } from 'uuid';

import { useAuthContext } from '../../../contexts/AuthContext';
import FHIRUpdater from '../../../data/FHIRUpdater';
import 'react-semantic-ui-datepickers/dist/react-semantic-ui-datepickers.css';
import usaStates from '../../../data/States';
import { FHIRUtils, ProvenanceBuilder } from '../../../fhir-sdoh';
import FHIRProxyClient from '../../../fhir-sdoh/common/fhirproxy-client';
import PatientCDO from '../../../models/patient';
import {
  buildAddressValidationErrors,
  resolveStateAbbrev,
  validateAddresses,
} from '../../../services/address-validation';
import type { AddressValidationItem, PostalAddress } from '../../../types/addressValidation';

import type { EditPatientProps, PatientEditDraft } from './types';
import { PreparePatientPayLoad, ValidatePatient, type PatientPayload } from './utils';

// Aliased: this module also declares a COMPONENT named `Patient` (the default
// export). Importing the FHIR resource type under the same name shadowed it, so
// `export default Patient` resolved to the type and every consumer's
// `import EditPatient from '../edit'` was a type-only import (TS1485).
import type { Address, BundleEntry, FhirResource, Identifier, Provenance } from 'fhir/r4';
import moment from 'moment';
import FHIRFetcher from 'data/FHIRFetcher';


const genderOptions = [
  { key: 'm', text: 'Male', value: 'Male' },
  { key: 'f', text: 'Female', value: 'Female' },
  { key: 'na', text: 'Unknown', value: 'Unknown' },
];

/**
 * The patient-edit form's own STATE.
 *
 * `PatientEditDraft` is the shape the `patient` prop carries — the seeding
 * effect writes the form-only fields onto the object the producer passed in and
 * then stores that very object here. The index signature on top of it is for
 * the computed-key trim loop in `validatePatientDetails`.
 *
 * SUSPECTED DEFECT (type-level, pre-existing): an interface without an index
 * signature is not assignable to one, so `PatientCDOModel` does not satisfy
 * this intersection — the `useState(PatientCDO)` initialiser below has failed
 * to typecheck since before this annotation, and the same mismatch now shows at
 * `setPatientDetails(flatPatient)`. Both are the same statement: the form's
 * state claims an index signature that neither of its two real sources has.
 * Removing the intersection fixes the assignability and breaks the trim loop;
 * rewriting the loop to index through an aliased view is a behaviour change
 * (verified against the emit — it adds a binding and renames every access), so
 * it is reported rather than made.
 */
type PatientDetailsDraft = PatientEditDraft & Record<string, unknown>;

const genderConervension: Record<string, string | undefined> = {
  male: 'Male',
  female: 'Female',
  unknown: 'Unknown',
};

// UI12: modal flag 'open' passed here from PatientCard
function Patient(props: EditPatientProps) {
  const authInfo = useAuthContext();
  const practitionerName = authInfo.practitionerName;

  const [dimmerProcess, setDimmerProcess] = useState(false);
  const [validationErrors, setValidationErrors] = useState<string[]>([]);
  const [displayDeliveryAddress, setDisplayDeliveryAddress] = useState(true);
  const [confirmOpen, setConfirmOpen] = useState(false); //open the popup window to confirm to create the patient
  const [confirmToCreatePatient, setConfirmToCreatePatient] = useState(false);
  // `useState(undefined)` alone infers the type `undefined`, which rejects the
  // eligibility-group string this is set to below.
  const [selectedEligibilityGroup, setSelectedEligibilityGroup] = useState<string | undefined>(undefined);
  const open = props.open;
  const flatPatient = props.patient;
  const patientId = props.patientId;
  const setFlag = props.toggleModal;
  const operation = props.operation;

  useEffect(() => {
    if (confirmToCreatePatient) {
      //if the confirmToCreatePatient is set to true, need to update validatePatientDetails with the updated confirmToCreatePatient state
      // eslint-disable-next-line @typescript-eslint/no-floating-promises -- fire-and-forget revalidation; awaiting it would order this render behind a refetch.
      validatePatientDetails();
    }
  }, [confirmToCreatePatient]);

  const [patientDate, setPatientDate] = useState(moment('1990-01-01').toDate());

  // Object template for patient builder to create Patient object
  // Cast, not a widened state type: the intersection is what makes the trim loop's
  // `patientDetails[property]` reads legal, and dropping it would push the change
  // into the loop. See the SUSPECTED DEFECT note on `PatientDetailsDraft`.
  const [patientDetails, setPatientDetails] = useState<PatientDetailsDraft>(PatientCDO as PatientDetailsDraft);

  // Default values population for Country and DOB
  useEffect(() => {
    /*
     * SUSPECTED DEFECT — REPORTED, NOT FIXED. `country` and `delivery_country` are
     * `string[]`, so `=== ''` can never hold and that arm of each `||` is dead. The
     * 'USA' default is applied for an absent value but NOT for the array-wrapped
     * empty string (`['']`) the parser produces for an address with no country, so
     * such a member keeps a blank country out to the save. The casts state the
     * comparison the code performs; a `[0]` read would compare a different value.
     */
    flatPatient.country =
      flatPatient.country === undefined || (flatPatient.country as unknown as string) === ''
        ? ['USA']
        : flatPatient.country;
    flatPatient.delivery_country =
      flatPatient.delivery_country === undefined ||
      (flatPatient.delivery_country as unknown as string) === ''
        ? ['USA']
        : flatPatient.delivery_country;
    flatPatient.org = props.organization;
    /*
     * SUSPECTED DEFECT — REPORTED, NOT FIXED. `genderConervension` is an open-key
     * map, so a gender string it does not carry yields `undefined` and writes that
     * into a field the model declares `string`. The gender select then renders with
     * no selection and the save omits the field, silently dropping a gender the
     * member's record already had.
     */
    flatPatient.gender = flatPatient.gender
      ? (genderConervension[flatPatient.gender.toLowerCase()] as string)
      : '';
    setPatientDate(
      flatPatient.dob === undefined || flatPatient.dob === ''
        ? moment('1990-01-01').toDate()
        : moment(flatPatient.dob).toDate()
    );

    /*
     * Both assignments are no-ops: the enclosing `if` has already established the
     * element is truthy, so `x !== undefined && x` is `x` and the `false` arm of the
     * `string | false` is unreachable. Cast rather than simplified, because
     * simplifying removes emitted expressions.
     */
    if (flatPatient.state?.[0]) {
      flatPatient.state[0] = (flatPatient.state[0] !== undefined && flatPatient.state[0]) as string;
    }
    if (flatPatient.delivery_state?.[0]) {
      flatPatient.delivery_state[0] = (flatPatient.delivery_state[0] !== undefined &&
        flatPatient.delivery_state[0]) as string;
    }
    flatPatient.external_referral_checkbox = flatPatient?.external_referral_checkbox ?? ['false'];

    //update selected eligibility group when we manually create patient if there is one
    if (operation === 'Add') {
      if (props?.selectedEligibilityGroup) {
        flatPatient.eligibilityGroup = props?.selectedEligibilityGroup;
        setSelectedEligibilityGroup(props?.selectedEligibilityGroup);
      } else {
        // `eligibilityGroup` is declared `string` on the model; this writes
        // `undefined` into it, which is what clears the picker on the Add path.
        flatPatient.eligibilityGroup = undefined as unknown as string;
        setSelectedEligibilityGroup(undefined);
      }
    } else if (flatPatient && operation === 'Update') {
      //set selected eligibility group when we manually update patient,
      if (flatPatient.eligibilityGroup) {
        setSelectedEligibilityGroup(flatPatient.eligibilityGroup);
      }
    }

    setPatientDetails(flatPatient as PatientDetailsDraft);

    if (flatPatient && operation === 'Update') {
      if (
        flatPatient.addressLine1?.[0] === flatPatient.delivery_addressLine1?.[0] &&
        flatPatient.addressLine2?.[0]?.trim() === flatPatient.delivery_addressLine2?.[0] &&
        flatPatient.city?.[0] === flatPatient.delivery_city?.[0] &&
        flatPatient.state?.[0] === flatPatient.delivery_state?.[0] &&
        flatPatient.zip?.[0] === flatPatient.delivery_zip?.[0]
      ) {
        setDisplayDeliveryAddress(true);
      } else {
        setDisplayDeliveryAddress(false);
      }
    }
  }, [flatPatient, operation, props?.selectedEligibilityGroup]);

  const handleChange = (e: (React.SyntheticEvent<HTMLElement> & { target: HTMLInputElement }) | undefined, attributeName?: string) => {
    //let { name, value } = e.target;
    if (e && e.target) {
      let name = e.target.name;
      let value = '';
      if (attributeName === 'dob') {
        name = attributeName;
        // Cast rather than the bracket form `noPropertyAccessFromIndexSignature` asks
        // for: `d.testid` and `d['testid']` emit different JavaScript.
        const rawdob = (e.target.dataset as { testid?: string }).testid!.split('-');
        value = rawdob[3] + '/' + rawdob[4] + '/' + rawdob[2];
      } else if (attributeName === 'gender') {
        name = attributeName;
        value = e.target.textContent;
      } else if (attributeName === 'state' || attributeName === 'delivery_state') {
        name = attributeName;
        const selectedOption = usaStates.list.find((opt) => opt.text === e.target.textContent); //find the corresponding state abbreviation based on the state full name
        const selectedKey = selectedOption?.key; //save state as 2 letter abbreviation in the backend
        // `find` misses when the picker's label is not in `usaStates.list`, which
        // writes `undefined` into a `string` local and then into the form state.
        value = selectedKey as string;
      } else if (attributeName === 'external_referral_checkbox') {
        name = attributeName;
        value = e.target.checked.toString();
        // SUSPECTED DEFECT — REPORTED, NOT FIXED. `attributeName` is a string, so
        // `.name` is always `undefined` and this branch never runs. The email
        // substitution it guards (`food@` → `noemail@`) is therefore dead: the
        // address is saved as typed.
      } else if ((attributeName as unknown as { name?: string })?.name === 'email') {
        //replace 'food@nourishedrx.com' with 'noemail@nourishedrx.com' when user input email
        if (e?.target?.value?.toLowerCase() === 'food@nourishedrx.com') {
          value = 'noemail@nourishedrx.com';
        } else {
          value = e.target.value;
        }
      } else {
        value = e.target.value;
      }
      const textObjs = ['org', 'dob', 'gender'];
      // Pass the data as an array for objects other than dob, gender and Organization
      setPatientDetails((prevState: PatientDetailsDraft) => ({
        ...prevState,
        [name]: textObjs.includes(name) ? value : [value],
      }));
    }
  };

  // Validation of Form fields
  const validatePatientDetails = async () => {
    for (const property in patientDetails) {
      if (Array.isArray(patientDetails[property]) && patientDetails[property][0] !== undefined) {
        // The index-signature read is `unknown`; the loop's own `Array.isArray`
        // guard is what establishes the array, and the element is a string wherever
        // this trims. A guard on the element would emit.
        (patientDetails[property] as string[])[0] = (patientDetails[property] as string[])[0]!.trim();
      } else if (typeof patientDetails[property] === 'string' && patientDetails[property] !== undefined) {
        patientDetails[property] = patientDetails[property].trim();
      }
    }
    let tempConfirmToCreatePatient = confirmToCreatePatient; //used to update the confirmToCreatePatient but not update the state

    let patientPayLoad = PreparePatientPayLoad(
      patientDetails,
      operation,
      practitionerName,
      displayDeliveryAddress,
      // `patientId` is `string | undefined` — `''` from the Add producer, and the
      // Update producer forwards `Patient.id`, which FHIR leaves `0..1`. Both
      // consumers below declare `string`.
      patientId!,
      props.careteamStatus!,
      props?.fhirPatient //patient original data
    );

    setValidationErrors((_state) => []);
    const validations = ValidatePatient(
      patientDetails,
      operation,
      practitionerName,
      displayDeliveryAddress,
      patientId!
    );
    setValidationErrors(validations.errors);
    if (validations.valid) {
      if (authInfo.validateUSPSAddress) {
        // `Patient.address` is `0..*`; a payload built with none reaches `.slice`
        // and `.map` below unguarded, which throws while validating rather than
        // skipping validation.
        const itemsToValidate = displayDeliveryAddress
          ? patientPayLoad.address!.slice(1, 2)
          : patientPayLoad.address!;

        const addressTypeById = new Map<string, string>();
        const requestItems: AddressValidationItem[] = itemsToValidate.map((item: Address, idx: number) => {
          // `Address.type` is `0..1`; an address without one produces the id
          // "undefined-0" and stores `undefined` as its type, which the result
          // labelling below then reads back.
          const id = `${item.type}-${idx}`;
          addressTypeById.set(id, item.type!);
          return {
            id,
            purpose: item.type === 'physical' ? 'shipping' : 'mailing',
            // Payer org id drives program-specific policy (e.g. PO Box bypass).
            ...(patientDetails.org ? { programId: patientDetails.org } : {}),
            // `PostalAddress` declares `locality`/`postalCode` as bare `?:`, which
            // `exactOptionalPropertyTypes` will not accept an `undefined` through.
            // `Address.city`/`.postalCode` are `0..1`, so absence is real and is
            // sent as `undefined` today rather than as an omitted key.
            address: {
              regionCode: 'US',
              addressLines: [item.line?.[0], item.line?.[1]].filter(Boolean) as string[],
              locality: item?.city,
              // `!` on an optional chain: the chain guards `item`, the `!` the
              // `state` that FHIR leaves `0..1`. Both arms are upstream's.
              // eslint-disable-next-line @typescript-eslint/no-non-null-asserted-optional-chain
              administrativeArea: resolveStateAbbrev(item?.state!),
              postalCode: item?.postalCode,
            } as PostalAddress,
          };
        });

        try {
          const response = await validateAddresses(requestItems);
          let hasErrors = false;

          response.results.forEach((result) => {
            const payloadItem = itemsToValidate[result.inputIndex];
            const typeLabel = payloadItem?.type === 'physical' ? 'Delivery Address: ' : 'Mailing Address: ';
            const input = requestItems[result.inputIndex]!.address;

            // On CORRECTED, USPS produced a deliverable variant. Write it back
            // into the form (delivery_* for physical, plain keys for mailing)
            // and ask the user to review + resubmit, blocking this save pass.
            if (result.verdict === 'CORRECTED') {
              const suggestion = result.suggestion ?? result.normalized;
              if (suggestion) {
                const prefix = payloadItem?.type === 'physical' ? 'delivery_' : '';
                const zip5 = suggestion.postalCode?.split('-')[0];
                setPatientDetails((prevState: PatientDetailsDraft) => ({
                  ...prevState,
                  ...(suggestion.line1 !== undefined ? { [`${prefix}addressLine1`]: [suggestion.line1 ?? ''] } : {}),
                  [`${prefix}addressLine2`]: [suggestion.line2 ?? ''],
                  ...(suggestion.city !== undefined ? { [`${prefix}city`]: [suggestion.city ?? ''] } : {}),
                  ...(suggestion.region !== undefined ? { [`${prefix}state`]: [suggestion.region ?? ''] } : {}),
                  ...(zip5 !== undefined ? { [`${prefix}zip`]: [zip5 ?? ''] } : {}),
                }));
                hasErrors = true;
                setValidationErrors((state) => [
                  ...state,
                  typeLabel + 'Address corrected by USPS. Please review the updated fields and submit again.',
                ]);
                return;
              }
            }

            const messages = buildAddressValidationErrors(result, {
              addressLine1: input.addressLines?.[0],
              addressLine2: input.addressLines?.[1],
              city: input.locality,
              stateAbbrev: input.administrativeArea,
              zip: input.postalCode,
            });

            if (messages.length) {
              hasErrors = true;
              messages.forEach((msg) => {
                setValidationErrors((state) => [...state, typeLabel + msg]);
              });
            }
          });

          if (!hasErrors) {
            const updatedPatientInfo = await validatePatientId(patientPayLoad, tempConfirmToCreatePatient);
            patientPayLoad = updatedPatientInfo.patientPayLoad;
            tempConfirmToCreatePatient = updatedPatientInfo.tempConfirmToCreatePatient;
            if (tempConfirmToCreatePatient) {
              // eslint-disable-next-line @typescript-eslint/no-floating-promises -- fire-and-forget revalidation; awaiting it would order this render behind a refetch.
              savePatient(authInfo.token, patientPayLoad);
            }
          }
        } catch (error) {
          console.log(error);
          setValidationErrors((state) => [...state, 'Unable to validate address']);
        }
      } else if (!authInfo.validateUSPSAddress) {
        //validate and confirm patient id
        const updatedPatientInfo = await validatePatientId(patientPayLoad, tempConfirmToCreatePatient);
        patientPayLoad = updatedPatientInfo.patientPayLoad;
        tempConfirmToCreatePatient = updatedPatientInfo.tempConfirmToCreatePatient;
        if (tempConfirmToCreatePatient) {
          // eslint-disable-next-line @typescript-eslint/no-floating-promises -- fire-and-forget revalidation; awaiting it would order this render behind a refetch.
          savePatient(authInfo.token, patientPayLoad);
        }
      }
    }
  };

  //below function checks if the patient with same member id exists in the fhirstore, then show popup window to confirm if it's okay to create the member
  //if yes, then it will add orgId with memberId before hash it
  // `PatientPayload`, not `Patient`: the producer rebuilds `communication`, `name`
  // and `telecom` as plain literals, so it never makes the `Patient` narrowing.
  const validatePatientId = async (patientPayLoad: PatientPayload, tempConfirmToCreatePatient: boolean) => {
    setDimmerProcess(true);
    if (operation === 'Add') {
      //check if the patient with same member id exists in the fhirstore
      const orgId = patientPayLoad?.managingOrganization?.reference?.split('/')?.[1];
      const memberId = patientPayLoad?.identifier?.find(
        (i: Identifier) => i?.system === 'https://projectwell.io/fhir/identifiers/member-id'
      )?.value;

      const key = memberId && orgId && `/Patient/?${`organization=${orgId}&_content:contains=${memberId}`}`;
      if (key) {
        //check in the payer patient or the pw fhirstore
        const patientResult = await FHIRFetcher(key, authInfo.token);
        //if patient with same member id exists, show popup to confirm if it's okay to create the patient
        if ((patientResult?.total ?? 0) > 0 && !confirmToCreatePatient) {
          //patient with the same member id exists
          setConfirmOpen(true);
        } else {
          //if the patient doesn't exist, we don't need to confirm to create the patient, set tempConfirmToCreatePatient to true
          tempConfirmToCreatePatient = true;
        }

        if ((patientResult?.total ?? 0) > 0 && confirmToCreatePatient) {
          //using GUID if patient exists and confirm to create patient
          patientPayLoad.id = uuid();
        }
      } else if (orgId === 'LYNX-OTC') {
        tempConfirmToCreatePatient = true;
      } else if (orgId !== 'LYNX-OTC') {
        //lynx don't have member id
        setValidationErrors((state) => [
          ...state,
          `${!orgId ? 'Organization' : 'Member id'} is missing. please update.`,
        ]);
      }
    } else {
      //when we update the patient information, always set confirmToCreatePatient to true as the member already exist in the fhirstore
      tempConfirmToCreatePatient = true;
    }
    return { patientPayLoad, tempConfirmToCreatePatient };
  };

  // FHIR Call to update patient details
  const savePatient = async (token: string, patientPayLoad: PatientPayload) => {
    setDimmerProcess(true);

    const proxyURL = import.meta.env.VITE_API_ENDPOINT + '/fhir/';
    const fhirProxyClient = new FHIRProxyClient(authInfo.token, proxyURL);
    const fhirBundle: FhirResource = FHIRUtils.buildEmptyBundle('transaction');
    const bundleEntries: BundleEntry[] = [];

    //create Provenance
    const provenance: Provenance = ProvenanceBuilder.build(
      `Patient/${patientPayLoad.id!}`,
      {
        reference: 'Practitioner/' + authInfo.practitionerId,
        display: practitionerName,
      }, //eg. {Practitioner/oa2,johnsmith},
      // `Patient.id` is `0..1`; a create has none until the store assigns one, so
      // the Provenance built here points at "Patient/undefined" on the Add path.
      patientPayLoad.id,
      operation === 'Update' ? 'UPDATE' : 'CREATE',
      `Patient has been ${operation === 'Update' ? 'updated' : 'created'}`,
      {
        code: patientDetails.org.toUpperCase(),
        display: patientDetails.org.toLowerCase(),
      } //eg. {Organization/HUMANA,humana}
    );
    provenance.target.splice(1, 1);
    bundleEntries.push(FHIRUtils.buildResourceEntry(provenance, false)); //Provenance

    //now post bundle request
    fhirBundle.entry = bundleEntries;
    await FHIRUpdater('/Patient/' + patientPayLoad.id, token, patientPayLoad).then(
      async (response) => {
        setDimmerProcess(false);
        setFlag(false, response);
        // SUSPECTED DEFECT — REPORTED, NOT FIXED. The guard tests
        // `rerefreshPatientDetails` (double "re") and the call is
        // `refreshPatientDetails`. No producer passes the misspelled prop, so the
        // refresh never fires; were one to pass it, the call would throw because
        // `refreshPatientDetails` is itself optional.
        if (props?.rerefreshPatientDetails) {
          props.refreshPatientDetails!();
        }
        await fhirProxyClient.send('post', '', fhirBundle).then(
          (_response) => {},
          (_error) => {
            // No operation needed on success/fail case
          }
        );
      },
      (_error) => {
        setDimmerProcess(false);
        setValidationErrors((state) => [
          ...state,
          'Unable to ' + operation.toLowerCase() + ' patient details...Please check logs',
        ]);
      }
    );
  };

  // Populate validation/error messages on the form
  const setValidationMessages = () => {
    return validationErrors.map((error) => {
      return <Message.Item>{error}</Message.Item>;
    });
  };

  return (
    <div>
      <Modal
        closeOnEscape={false}
        closeOnDimmerClick={false}
        //style={{height: '95%'}}
        open={open}
        onClose={() => setFlag(false)}
        onOpen={() => setFlag(true)}
      >
        <Header icon="edit" color="red" content={operation + ' Patient Details'} />

        <Modal.Content>
          {validationErrors.length > 0 && (
            <Message error>
              <Message.Header>Error:</Message.Header>
              <Message.List>{setValidationMessages()}</Message.List>
            </Message>
          )}

          <Form className="patientForm" onSubmit={validatePatientDetails} autoComplete="off" error>
            <Form.Group>
              <Form.Field
                id={'external-referral-checkbox'}
                width={8}
                control={Checkbox}
                label={'External Referral'}
                onChange={(e: React.SyntheticEvent<HTMLElement> & { target: HTMLInputElement }) => handleChange(e, 'external_referral_checkbox')}
                checked={
                  patientDetails?.external_referral_checkbox?.[0]
                    ? patientDetails?.external_referral_checkbox?.[0] === 'true'
                      ? true
                      : false
                    : false
                }
                value={
                  patientDetails?.external_referral_checkbox
                    ? // SUSPECTED DEFECT — REPORTED, NOT FIXED.
                      // `external_referral_checkbox` is `string[]`, so `=== 'true'`
                      // never holds and the checkbox renders unchecked for a member
                      // whose flag is `['true']`.
                      (patientDetails?.external_referral_checkbox as unknown as string) === 'true'
                      ? true
                      : false
                    : false
                }
                name="externalReferral"
                style={{ fontWeight: 'bold' }}
              ></Form.Field>
            </Form.Group>
            <Form.Group>
              <Form.Field
                id="form-input-control-first-name"
                control={Input}
                label="First Name"
                width={8}
                placeholder="First Name"
                name="firstName"
                value={patientDetails.firstName[0]}
                onChange={handleChange}
                required
              />
              <Form.Field
                id="form-input-control-middle-name"
                control={Input}
                width={2}
                label="Middle Name"
                placeholder="Middle Name"
                name="middleName"
                value={patientDetails.middleName[0]}
                onChange={handleChange}
              />
              <Form.Field
                id="form-input-control-last-name"
                control={Input}
                width={6}
                label="Last Name"
                placeholder="Last Name"
                name="lastName"
                value={patientDetails.lastName[0]}
                onChange={handleChange}
                required
              />
            </Form.Group>

            <Form.Group widths="equal">
              <Form.Field
                id="form-input-control-memberid"
                control={Input}
                label="Member Id"
                name="memberId"
                onChange={handleChange}
                value={patientDetails?.memberId?.[0]}
                readOnly={operation === 'Update'}
                required={patientDetails.org !== 'LYNX-OTC'} //member id is not a mandatory field for lynx
              />
              <Form.Field
                id="form-input-control-otherid"
                control={Input}
                label="Other Id"
                name="otherId"
                onChange={handleChange}
                value={patientDetails?.otherId?.[0]}
                readOnly={patientDetails.org === 'BCSC-MCARE-PD'}
              />
              <Form.Field
                id="form-input-control-personid"
                control={Input}
                label="Person Id"
                name="personId"
                value={patientDetails?.personId?.[0] || ''}
                readOnly
              />
            </Form.Group>
            <Form.Group widths="equal">
              <Form.Field
                id="form-input-control-organization"
                control={Input}
                label="Organization"
                placeholder="Organization"
                name="org"
                value={patientDetails.org}
                onChange={handleChange}
                readOnly
                required
              />
              <Form.Field
                id="form-input-control-provider"
                control={Input}
                label="Provider"
                placeholder="Provider"
                name="provider"
                value={patientDetails.provider ? patientDetails.provider : ''}
                onChange={handleChange}
              />
              <Form.Field
                id="form-input-control-eligbility-group"
                control={Input}
                label="Eligiblity Group"
                name="eligibligtyGroup"
                value={selectedEligibilityGroup}
                readOnly
              />
            </Form.Group>

            <Form.Group widths="equal">
              <Form.Field
                id="form-select-control-gender"
                required
                control={Select}
                options={genderOptions}
                label={{
                  children: 'Gender',
                  htmlFor: 'form-select-control-gender',
                }}
                placeholder="Gender"
                name="gender"
                value={patientDetails.gender}
                onChange={(e: React.SyntheticEvent<HTMLElement> & { target: HTMLInputElement }) => handleChange(e, 'gender')}
                search
                searchInput={{ id: 'form-select-control-gender' }}
              />
              <Form.Field
                id="form-input-control-language"
                control={Input}
                label="Language"
                placeholder="language"
                name="language"
                value={patientDetails.language[0]}
                onChange={handleChange}
              />
            </Form.Group>
            <Form.Group widths="equal">
              <Form.Field
                id="form-input-control-home-phone"
                control={Input}
                label="Home Phone"
                placeholder="Home Phone"
                name="homePhone"
                value={patientDetails.homePhone[0]}
                onChange={(e: React.SyntheticEvent<HTMLElement> & { target: HTMLInputElement }) => handleChange(e, 'phone')}
              />
              <Form.Field
                id="form-input-control-mobile-phone"
                control={Input}
                label="Mobile Phone"
                placeholder="Mobile Phone"
                name="mobilePhone"
                value={patientDetails.mobilePhone[0]}
                onChange={(e: React.SyntheticEvent<HTMLElement> & { target: HTMLInputElement }) => handleChange(e, 'phone')}
              />
            </Form.Group>
            <Form.Group widths="equal">
              <SemanticDatepicker
                required
                autoComplete="false"
                datePickerOnly
                name="dob"
                value={patientDate}
                clearable={false}
                format="MM/DD/YYYY"
                id="form-input-control-dob"
                label="Date Of Birth"
                // The datepicker supplies `SyntheticEvent | undefined`, which
                // `handleChange` does not accept. On a cleared date the `undefined`
                // arm reaches `e.target.dataset` and throws — existing behaviour, kept
                // by casting rather than guarding.
                onChange={
                  ((e: React.SyntheticEvent<HTMLElement> & { target: HTMLInputElement }) =>
                    handleChange(e, 'dob')) as unknown as SemanticDatepickerProps['onChange']
                }
                className="dobCalendar"
              />

              <Form.Field
                id="form-input-control-email"
                control={Input}
                label="Email"
                placeholder="Email"
                name="email"
                value={patientDetails.email[0]}
                onChange={handleChange}
              />
            </Form.Group>

            <Form.Group>
              <Form.Field id="form-input-control-mailingaddress-label" size="large" label="Mailing Address: " />
            </Form.Group>
            <Segment>
              <Form.Group>
                <Form.Field
                  id="form-input-control-addressLine1"
                  control={Input}
                  width={8}
                  label="Address Line 1"
                  placeholder="Address Line 1"
                  name="addressLine1"
                  required
                  value={patientDetails.addressLine1?.[0]}
                  onChange={handleChange}
                />

                <Form.Field
                  id="form-input-control-addressLine2"
                  control={Input}
                  width={8}
                  label="Address Line 2"
                  placeholder="Address Line 2"
                  name="addressLine2"
                  value={patientDetails.addressLine2?.[0]}
                  onChange={handleChange}
                />
              </Form.Group>
              <Form.Group widths="equal">
                <Form.Field
                  id="form-input-control-city"
                  control={Input}
                  label="City"
                  placeholder="City"
                  name="city"
                  required
                  value={patientDetails.city?.[0]}
                  onChange={handleChange}
                />

                <Form.Field
                  id="form-select-control-state"
                  control={Select}
                  options={usaStates.list}
                  label={{
                    children: 'State',
                    htmlFor: 'form-select-control-state',
                  }}
                  placeholder="State"
                  name="state"
                  value={normalizeStateToText(patientDetails.state?.[0])}
                  onChange={(e: React.SyntheticEvent<HTMLElement> & { target: HTMLInputElement }) => handleChange(e, 'state')}
                  search
                  required
                  searchInput={{ id: 'form-select-control-state' }}
                />
              </Form.Group>
              <Form.Group widths="equal">
                <Form.Field
                  id="form-input-control-zip"
                  control={Input}
                  label="Zip"
                  placeholder="Zip"
                  name="zip"
                  value={patientDetails.zip?.[0]}
                  required
                  onChange={handleChange}
                />
                <Form.Field
                  id="form-input-control-country"
                  control={Input}
                  label="Country"
                  placeholder="Country"
                  name="country"
                  value={patientDetails.country?.[0]}
                  onChange={handleChange}
                  required
                  readOnly
                />
              </Form.Group>
            </Segment>

            <Form.Group>
              <Form.Field>
                <Checkbox
                  checked={displayDeliveryAddress}
                  onChange={(_e) => setDisplayDeliveryAddress(!displayDeliveryAddress)}
                  label="Delivery Address same as Mailing Address"
                />
              </Form.Field>
            </Form.Group>
            {!displayDeliveryAddress && (
              <div>
                <Form.Group size="massive">
                  <Form.Field id="form-input-control-mailingaddress-label" size="massive" label="Delivery Address: " />
                </Form.Group>
                <Segment>
                  <Form.Group>
                    <Form.Field
                      id="form-input-control-delivery_addressLine1"
                      control={Input}
                      width={8}
                      label="Address Line 1"
                      placeholder="Address Line 1"
                      name="delivery_addressLine1"
                      required
                      value={patientDetails.delivery_addressLine1?.[0]}
                      onChange={handleChange}
                    />

                    <Form.Field
                      id="form-input-control-delivery_addressLine2"
                      control={Input}
                      width={8}
                      label="Address Line 2"
                      placeholder="Address Line 2"
                      name="delivery_addressLine2"
                      value={patientDetails.delivery_addressLine2?.[0]}
                      onChange={handleChange}
                    />
                  </Form.Group>
                  <Form.Group widths="equal">
                    <Form.Field
                      id="form-input-control-delivery_city"
                      control={Input}
                      label="City"
                      placeholder="City"
                      name="delivery_city"
                      required
                      value={patientDetails.delivery_city?.[0]}
                      onChange={handleChange}
                    />

                    <Form.Field
                      id="form-select-control-delivery_state"
                      control={Select}
                      options={usaStates.list}
                      label={{
                        children: 'State',
                        htmlFor: 'form-select-control-state',
                      }}
                      placeholder="State"
                      name="delivery_state"
                      value={normalizeStateToText(patientDetails.delivery_state?.[0])}
                      onChange={(e: React.SyntheticEvent<HTMLElement> & { target: HTMLInputElement }) => handleChange(e, 'delivery_state')}
                      search
                      required
                      searchInput={{ id: 'form-select-control-delivery_state' }}
                    />
                  </Form.Group>
                  <Form.Group widths="equal">
                    <Form.Field
                      id="form-input-control-delivery_zip"
                      control={Input}
                      label="Zip"
                      placeholder="Zip"
                      name="delivery_zip"
                      value={patientDetails.delivery_zip?.[0]}
                      required
                      onChange={handleChange}
                    />
                    <Form.Field
                      id="form-input-control-delivery_country"
                      control={Input}
                      label="Country"
                      placeholder="Country"
                      name="delivery_country"
                      value={patientDetails.delivery_country?.[0]}
                      onChange={handleChange}
                      required
                      readOnly
                    />
                  </Form.Group>
                </Segment>
              </div>
            )}
            <Modal.Actions style={{ textAlign: 'center' }}>
              <Button color="red" onClick={() => setFlag(false)}>
                <Icon name="remove" /> Cancel
              </Button>
              <Button color="green" type="submit">
                <Icon name="checkmark" /> {operation}
              </Button>
            </Modal.Actions>
          </Form>
          <Dimmer active={dimmerProcess}>
            <Loader />
          </Dimmer>
          <Confirm
            content="The patient with same member id exsits, are you sure that you still want to create this member?"
            open={confirmOpen}
            closeOnDimmerClick={false}
            onCancel={() => {
              setConfirmOpen(false);
              setDimmerProcess(false);
            }}
            onConfirm={() => {
              setConfirmToCreatePatient(true);
              setConfirmOpen(false);
            }}
            style={{ marginTop: '20%' }}
          />
        </Modal.Content>
      </Modal>
    </div>
  );
}

export default Patient;

//this function is used to convert the state value in the patient details to the full state name to display in the dropdown, for example, convert "AL" to "Alabama", because the dropdown options are in full state name format
const normalizeStateToText = (stateValue: string | undefined) => {
  if (!stateValue) return undefined;

  const normalized = stateValue.toUpperCase();

  return usaStates.list.find(
    (s) =>
      s.key === normalized || // "AL"
      s.text.toUpperCase() === normalized // "ALABAMA"
  )?.text;
};
